import {
  type PlanComponent,
  type PlanServiceNestedUniverse,
  type PlanServicePlanComponents,
  type PricingConfig,
  type RecipeQuantityPricingStrategy,
  type ServingSize,
} from '@hungryroot/rest';

export const BREAKFAST_MEAL_CONTAINER_ID = 9;
export const LUNCH_CONTAINER_ID = 2;
export const DINNER_CONTAINER_ID = 3;
export const SNACKS_CONTAINER_ID = 4;
export const SWEETS_CONTAINER_ID = 5;

export const MEAL_CONTAINER_IDS = [
  BREAKFAST_MEAL_CONTAINER_ID,
  LUNCH_CONTAINER_ID,
  DINNER_CONTAINER_ID,
];

export const LUNCH_STABLE_NAME_ID = 'Lunch';
export const DINNER_STABLE_NAME_ID = 'Dinner';
export const KIDS_STABLE_NAME_ID = 'Kids';

export const MAX_RECIPES_2_SERVINGS = 8;
export const MAX_RECIPES_4_SERVINGS = 4;
export const MIN_RECIPES_2_SERVINGS = 3;
export const MIN_RECIPES_4_SERVINGS = 2;

export const getNumLunchAndNumDinnerDistribution = (
  servingSize: ServingSize,
  planData: Pick<PricingConfig, 'numLunch' | 'numDinner'>
) => {
  if (servingSize === 4) {
    // product-specific logic
    // if 2 serving user has 4 lunches and 4 dinners selected, switches to 4 servings, lunches and dinners should update to 2 each.
    if (planData.numLunch === 4 && planData.numDinner === 4) {
      return {
        numLunch: 2,
        numDinner: 2,
      };
      // product-specific logic
      // if 2-serving user has 8 recipes, and then updates to 4-serving plan, largest meal type should be reduced enough to bring total recipe max to 4
    } else if (
      planData.numLunch + planData.numDinner >
      MAX_RECIPES_4_SERVINGS
    ) {
      const effectiveNumDinner =
        planData.numDinner >= planData.numLunch
          ? MAX_RECIPES_4_SERVINGS - planData.numLunch
          : planData.numDinner;
      const effectiveNumLunch =
        planData.numLunch > planData.numDinner
          ? MAX_RECIPES_4_SERVINGS - planData.numDinner
          : planData.numLunch;
      return {
        numDinner: effectiveNumDinner,
        numLunch: effectiveNumLunch,
      };
    }
  } else if (servingSize === 2) {
    if (planData.numLunch === 1 && planData.numDinner === 1) {
      return {
        numLunch: 2,
        numDinner: 1,
      };
    } else if (planData.numLunch === 2 && planData.numDinner === 0) {
      return {
        numLunch: 3,
        numDinner: 0,
      };
    } else if (planData.numLunch === 0 && planData.numDinner === 2) {
      return {
        numLunch: 0,
        numDinner: 3,
      };
    }
  }

  return {
    numLunch: planData.numLunch,
    numDinner: planData.numDinner,
  };
};

export function convertPlanComponentsToEdit(
  planComponents: PlanServicePlanComponents['plan_components']
) {
  return planComponents.map((component) => {
    return {
      quantity: component.container_quantity,
      occasion_group_id: component.occasion_group_id,
      occasion_id: component.occasion_id,
      container_id: component.container_id,
      pricing_configurations: {
        ...(component.pricing_configurations.TIERED
          ? {
              TIERED: {
                version: component.pricing_configurations.TIERED.version,
              },
              SKU_QUANTITY: null,
              RECIPE_QUANTITY: null,
            }
          : {
              ...component.pricing_configurations,
            }),
      },
    };
  });
}

export const findContainer = (
  containerName: string,
  data: PlanServiceNestedUniverse
) => {
  const allOccasions = data.occasion_groups.flatMap((group) => group.occasions);

  const allContainers = allOccasions.flatMap((occasion) => occasion.containers);

  const container = allContainers.find(
    (container) => container.name === containerName
  );

  return container;
};

export const findPlanComponent = (
  planComponents: PlanComponent[],
  containerName: string
) => {
  return planComponents.find(
    (planComponent) => planComponent.container_name === containerName
  );
};

export const findPlanComponentByContainerId = (
  planComponents: PlanComponent[],
  containerId: number
) => {
  return planComponents.find(
    (planComponent) => planComponent.container_id === containerId
  );
};

export const setNextPlanComponents = ({
  planComponents,
  nextNumLunch,
  nextNumDinner,
  numLunch,
  numDinner,
  occasionId,
  containerId,
  dinnerContainerId,
  id,
  servingSize,
}: {
  planComponents: PlanServicePlanComponents;
  nextNumLunch: number;
  nextNumDinner: number;
  numLunch: number;
  numDinner: number;
  occasionId?: number;
  containerId?: number;
  dinnerContainerId?: number;
  id?: number;
  servingSize?: ServingSize;
}) => {
  const nextPlanComponents = planComponents.plan_components.reduce(
    (acc, planComponent) => {
      if (
        (planComponent?.container_name === LUNCH_STABLE_NAME_ID &&
          nextNumLunch === 0) ||
        (planComponent?.container_name === DINNER_STABLE_NAME_ID &&
          nextNumDinner === 0)
      ) {
        return acc;
      }

      const container_quantity =
        planComponent?.container_name === LUNCH_STABLE_NAME_ID
          ? nextNumLunch
          : planComponent?.container_name === DINNER_STABLE_NAME_ID
            ? nextNumDinner
            : planComponent.container_quantity;

      return [
        ...acc,
        {
          ...planComponent,
          container_quantity,
        },
      ];
    },
    [] as PlanServicePlanComponents['plan_components']
  );

  const effectiveNextPlanComponents =
    (numLunch === 0 || numDinner === 0) && nextPlanComponents
      ? [
          ...nextPlanComponents,
          {
            occasion_id: occasionId,
            occasion_group_id: id,
            container_name:
              numLunch === 0 ? LUNCH_STABLE_NAME_ID : DINNER_STABLE_NAME_ID,
            container_id: numLunch === 0 ? containerId : dinnerContainerId,
            container_quantity: numLunch === 0 ? nextNumLunch : nextNumDinner,
            pricing_configurations: {
              RECIPE_QUANTITY: {
                serving_size:
                  // use the existing container serving_size since that's required
                  // and will be the same
                  servingSize,
              },
            },
          } as PlanComponent,
        ]
      : nextPlanComponents;
  return effectiveNextPlanComponents;
};

export const setNextPlanComponentsForRecipeSize = ({
  planComponents,
  value,
  numLunch,
  numDinner,
}: {
  planComponents: PlanServicePlanComponents;
  value: ServingSize;
  numLunch: number;
  numDinner: number;
}) => {
  const nextPlanComponents = planComponents.plan_components.reduce(
    (acc, component) => {
      const { numLunch: nextNumLunch, numDinner: nextNumDinner } =
        getNumLunchAndNumDinnerDistribution(value, {
          numLunch,
          numDinner,
        });

      if (
        (component?.container_name === LUNCH_STABLE_NAME_ID &&
          nextNumLunch === 0) ||
        (component?.container_name === DINNER_STABLE_NAME_ID &&
          nextNumDinner === 0)
      ) {
        return acc;
      }

      let containerQuantity;
      let pricingConfigurations = component?.pricing_configurations;

      if (component?.container_name === LUNCH_STABLE_NAME_ID) {
        containerQuantity = nextNumLunch;
      } else if (component?.container_name === DINNER_STABLE_NAME_ID) {
        containerQuantity = nextNumDinner;
      }

      if (containerQuantity !== undefined) {
        // use this when adapting for different pricing strategies
        // const pricingStrategy =
        //   component?.container_pricing_strategy;
        pricingConfigurations = {
          ...pricingConfigurations,
          RECIPE_QUANTITY: {
            ...pricingConfigurations.RECIPE_QUANTITY,
            serving_size: value,
          } as RecipeQuantityPricingStrategy,
        };
      }

      return [
        ...acc,
        {
          ...component,
          ...(containerQuantity !== undefined && {
            container_quantity: containerQuantity,
          }),
          pricing_configurations: pricingConfigurations,
        },
      ];
    },
    [] as PlanServicePlanComponents['plan_components']
  );
  return nextPlanComponents;
};

export const setNextPlanComponentsForNonMealContainers = ({
  value,
  planComponents,
  containerId,
  containerIdToPlanComponent,
  occasionId,
  occasionGroupId,
}: {
  value: string;
  planComponents: PlanServicePlanComponents;
  containerId: number;
  containerIdToPlanComponent: Record<string, PlanComponent>;
  occasionId?: number;
  occasionGroupId?: number;
}) => {
  const numberValue = Number(value);
  const nextPlanComponents = planComponents.plan_components.reduce(
    (acc, component) => {
      if (component.container_id === containerId && numberValue === 0) {
        return acc;
      }

      return [
        ...acc,
        component.container_id !== containerId
          ? { ...component }
          : {
              ...component,
              container_quantity: numberValue,
              pricing_configurations: {
                ...component.pricing_configurations,
                TIERED: {
                  ...component.pricing_configurations.TIERED,
                  tier: numberValue,
                },
              },
            },
      ] as PlanComponent[];
    },
    [] as PlanComponent[]
  );

  const effectiveNextPlanComponents = !containerIdToPlanComponent?.[containerId]
    ? [
        ...nextPlanComponents,
        {
          occasion_id: occasionId,
          occasion_group_id: occasionGroupId,
          container_id: containerId,
          container_quantity: numberValue,
          pricing_configurations: {
            TIERED: {},
          },
        } as PlanComponent,
      ]
    : nextPlanComponents;

  return effectiveNextPlanComponents;
};

export const setNextPlanComponentsForQuantityContainers = ({
  quantity,
  servingSize,
  dinnerServingSize,
  planComponents,
  containerId,
  containerIdToPlanComponent,
  occasionId,
  occasionGroupId,
}: {
  quantity: number;
  servingSize: ServingSize;
  dinnerServingSize: ServingSize;
  planComponents: PlanServicePlanComponents;
  containerId: number;
  containerIdToPlanComponent: Record<string, PlanComponent>;
  occasionId?: number;
  occasionGroupId?: number;
}) => {
  const nextPlanComponents = planComponents.plan_components.reduce(
    (acc, component) => {
      // Lunch and breakfast meal containers should always use dinner serving size. If dinner is edited but lunch and breakfast are not, the lunch and breakfast containers need to be updated to use the new dinner serving size value
      const uneditedComponent =
        component.container_id === LUNCH_CONTAINER_ID ||
        component.container_id === BREAKFAST_MEAL_CONTAINER_ID
          ? {
              ...component,
              pricing_configurations: {
                ...component.pricing_configurations,
                RECIPE_QUANTITY: {
                  ...component.pricing_configurations.RECIPE_QUANTITY,
                  serving_size: dinnerServingSize,
                },
              },
            }
          : { ...component };

      return [
        ...acc,
        component.container_id !== containerId
          ? { ...uneditedComponent }
          : {
              ...component,
              container_quantity: quantity,
              pricing_configurations: {
                ...component.pricing_configurations,
                RECIPE_QUANTITY: {
                  ...component.pricing_configurations.RECIPE_QUANTITY,
                  serving_size:
                    component.container_name === LUNCH_STABLE_NAME_ID
                      ? dinnerServingSize
                      : servingSize,
                },
              },
            },
      ] as PlanComponent[];
    },
    [] as PlanComponent[]
  );

  const effectiveNextPlanComponents = !containerIdToPlanComponent?.[containerId]
    ? [
        ...nextPlanComponents,
        {
          occasion_id: occasionId,
          occasion_group_id: occasionGroupId,
          container_id: containerId,
          container_quantity: quantity,
          pricing_configurations: {
            RECIPE_QUANTITY: {
              serving_size: servingSize,
            },
          },
        } as PlanComponent,
      ]
    : nextPlanComponents;

  return effectiveNextPlanComponents;
};
